import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import 'react-phone-number-input/style.css';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Card, CardContent } from '@connected/ui/components/card';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { LoadingPage } from '@/components/loading-page';
import { Observation } from '@/components/observation';
import { PhoneInput } from '@/components/phone-input';
import { useUserSignature } from '@/features/user-settings/hooks/use-user-signature';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/_auth/dashboard/account/$userId/profile/',
)({
    component: ProfileRouteComponent,
});

type UserData = components['schemas']['EditAccountSettingsCommand'] & {
    File: File | null;
};

export function ProfileRouteComponent() {
    const { t } = useTranslation('dashboard');

    const { data, isError, isLoading, isFetched, error } = useQuery(portaalQueryOptions.accounts.view());
    const { currentUser } = useAuthentication();
    const auth = useAuthentication();

    const standardColors = ['#099250', '#1570EF', '#444CE7', '#6938EF', '#fcc200', '#008080', '#b22222', '#0F172B', '#C81CDE'];

    const signature = useUserSignature({
        userId: currentUser?.UserId ?? '',
        enabled: !!currentUser?.UserId,
    });

    const DEFAULT_VALUES = useMemo<UserData>(() => ({
        DisplayName: data?.Setting?.DisplayName ?? '',
        SignatureText: data?.Setting?.SignatureText ?? '',
        FirstName: data?.Setting?.FirstName ?? '',
        MiddleName: data?.Setting?.MiddleName ?? '',
        LastName: data?.Setting?.LastName ?? '',
        Department: data?.Setting?.Department ?? '',
        JobTitleDescription: data?.Setting?.JobTitleDescription ?? '',
        PhoneNumber: data?.Setting?.PhoneNumber ?? '',
        File: signature.selectedFile,
        ColorCode: data?.Setting?.ColorCode ?? '',
    }), [data?.Setting?.ColorCode, data?.Setting?.Department, data?.Setting?.DisplayName, data?.Setting?.FirstName, data?.Setting?.JobTitleDescription, data?.Setting?.LastName, data?.Setting?.MiddleName, data?.Setting?.PhoneNumber, data?.Setting?.SignatureText, signature.selectedFile]);

    const updateAccountSettings = api.useMutation(
        'put',
        '/api/v2/accounts/settings/edit',
        {
            onSuccess: () => {
                toast.success(t('dashboard.account.settings.message.updateSuccess'));
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'UpdateAccountSettings',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/accounts/settings/edit' },
                );

                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: async ({ value }) => {
            await updateAccountSettings.mutateAsync({
                body: {
                    DisplayName: value.DisplayName,
                    SignatureText: value.SignatureText,
                    FirstName: value.FirstName,
                    MiddleName: value.MiddleName,
                    LastName: value.LastName,
                    Department: value.Department,
                    JobTitleDescription: value.JobTitleDescription,
                    ColorCode: value.ColorCode,
                    PhoneNumber: value.PhoneNumber,
                },
            });

            await signature.handleSignatureSubmit(currentUser?.UserId ?? '');

            if (currentUser?.UserId) {
                await queryClient.invalidateQueries(
                    portaalQueryOptions.electrical.users.userFiles({
                        userId: currentUser.UserId,
                    }),
                );
            }
        },
    });

    if (isError && isFetched) {
        logger.error({
            system: 'ViewAccountSettings',
            error,
        }, { apiHttpMethod: 'Get', apiUrl: '/api/v2/accounts/settings/edit' });
        toast.error(isError ?? t('dashboard.general.messages.error'));
        return null;
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    if (isLoading || signature.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 grid-cols-1 w-full">
            <div>
                <p className="font-semibold text-sm">{t('dashboard.account.profile.title')}</p>
                <p className="text-sm">{t('dashboard.account.profile.subtitle')}</p>
            </div>
            <div className="w-full">
                <Card>
                    <CardContent className="mt-6 ml-1 mr-1">
                        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-1">
                            <form.AppForm>
                                <form.AppField
                                    name="DisplayName"
                                    validators={{
                                        onChange: validators.editAccountSettingsCommand.displayName,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.displayName')} />
                                            <field.Input placeholder={t('dashboard.users.table.displayNamePlaceholder')} />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                >
                                </form.AppField>

                                <form.AppField
                                    name="SignatureText"
                                    validators={{
                                        onChange: validators.editAccountSettingsCommand.signatureText,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.signatureText')} />
                                            <field.Input placeholder={t('dashboard.users.table.signatureTextPlaceholder')} />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                >
                                </form.AppField>

                                {signature.signatureLoaded && (
                                    <form.AppField
                                        name="File"
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={!signature.selectedFile ? t('dashboard.users.table.signatureUpload') : t('dashboard.users.table.signatureFile')} />
                                                <div>
                                                    {!signature.selectedFile && (
                                                        <FileInput
                                                            maxFileSize={signature.validation?.MaxFileSize}
                                                            minFileSize={signature.validation?.MinFileSize}
                                                            maxFileName={signature.validation?.MaxFileNameLength}
                                                            onChange={(files) => {
                                                                signature.setSelectedFile(files[0]);
                                                                form.setFieldValue('File', files[0]);
                                                                signature.setUpdatedFile(true);
                                                                signature.setSignatureRemoved(false);
                                                            }}
                                                            accept="image/jpeg, image/png, image/webp, image/bmp, image/svg+xml"
                                                        />
                                                    )}
                                                    {signature.selectedFile && (
                                                        <div className="flex justify-center relative">
                                                            <img
                                                                className="max-h-32"
                                                                src={URL.createObjectURL(signature.selectedFile)}
                                                                alt="signature"
                                                            />
                                                            <Button
                                                                variant="ghost"
                                                                className="absolute right-0 top-1"
                                                                onClick={() => {
                                                                    signature.setSelectedFile(null);
                                                                    form.setFieldValue('File', null);
                                                                    signature.setUpdatedFile(false);
                                                                    signature.setSignatureRemoved(true);
                                                                }}
                                                            >
                                                                <Trash2 size={22} />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                )}

                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <form.AppField
                                            name="FirstName"
                                            validators={{
                                                onChange: validators.editAccountSettingsCommand.firstName,
                                            }}
                                            children={field => (
                                                <field.Label>
                                                    <field.Label.Title label={t('dashboard.users.table.firstName')} />
                                                    <field.Input
                                                        placeholder={t('dashboard.users.table.firstNamePlaceHolder')}
                                                    />
                                                    <field.ValidationError />
                                                </field.Label>
                                            )}
                                        >
                                        </form.AppField>
                                    </div>
                                    <div className="basis-1">
                                        <form.AppField
                                            name="MiddleName"
                                            validators={{
                                                onChange: validators.editAccountSettingsCommand.middleName,
                                            }}
                                            children={field => (
                                                <field.Label>
                                                    <field.Label.Title label={t('dashboard.users.table.middleName')} />
                                                    <field.Input
                                                        placeholder={t('dashboard.users.table.middleNamerPlaceHolder')}
                                                    />
                                                    <field.ValidationError />
                                                </field.Label>
                                            )}
                                        >
                                        </form.AppField>
                                    </div>
                                </div>

                                <form.AppField
                                    name="LastName"
                                    validators={{
                                        onChange: validators.editAccountSettingsCommand.lastName,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.lastName')} />
                                            <field.Input
                                                placeholder={t('dashboard.users.table.lastNamePlaceholder')}
                                            />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                >
                                </form.AppField>

                                {auth?.currentUser?.Roles?.some(x => x.Name?.toLowerCase().includes('inspecteur'))
                                    && (
                                        <form.AppField
                                            name="ColorCode"
                                            children={field => (
                                                <field.Label asDiv>
                                                    <field.Label.Title label={t('dashboard.users.table.colorCode')} />
                                                    <div className="flex flex-row gap-2 mb-2">
                                                        {standardColors.map(color => (
                                                            <div
                                                                key={color}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    field.handleChange(color);
                                                                }}
                                                                className={`cursor-pointer rounded-full p-0.5 ${field.state.value === color ? 'ring-1 ring-primary ring-offset-1' : ''}`}
                                                            >
                                                                <Observation color={color} label="" children />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </field.Label>
                                            )}
                                        />
                                    )}

                                <form.AppField
                                    name="Department"
                                    validators={{
                                        onChange: validators.editAccountSettingsCommand.department,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.department')} />
                                            <field.Input
                                                placeholder={t('dashboard.users.table.departmentPlaceholder')}
                                            />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                >
                                </form.AppField>

                                <form.AppField
                                    name="JobTitleDescription"
                                    validators={{
                                        onChange: validators.editAccountSettingsCommand.jobTitleDescription,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.jobTitle')} />
                                            <field.Input
                                                placeholder={t('dashboard.users.table.jobTitlePlaceholder')}
                                            />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                >
                                </form.AppField>

                                <form.AppField
                                    name="PhoneNumber"
                                    validators={{
                                        onSubmit: ({ value }) => {
                                            if (!value) {
                                                return null;
                                            }

                                            const phoneRegex
                                                = /^(?:\+?(?:31|32)|0|6)\d{7,15}$/;
                                            if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
                                                return t('dashboard.users.table.error.phoneInvalid');
                                            }

                                            return null;
                                        },
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.phoneNumber')} />
                                            <PhoneInput value={field.state.value ?? ''} onChange={field.handleChange} countries={['NL', 'BE']} defaultCountry={data?.Setting?.PhoneNumber?.startsWith('+31') ? 'NL' : data?.Setting?.PhoneNumber?.startsWith('+32') ? 'BE' : 'NL'} />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                />

                                <div className="flex space-x-2 w-full justify-end bg-transparent border-t-1 mt-2">
                                    <form.SubmitButton>
                                        {t('dashboard.save')}
                                    </form.SubmitButton>
                                </div>
                            </form.AppForm>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
